import React, { useEffect, useState } from "react";
import { Card, Select, Calendar, Modal, Button, Row, Col, Tag, Descriptions, Radio, Input, Popconfirm, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { formatTimeInTz, tzLabel } from "../../helper/timezone";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import StatCard from "../../components/ui/StatCard";
import EmptyState from "../../components/ui/EmptyState";

// colour + label for each day status (matches the rest of the portal)
const STATUS = {
  present: { color: "#14B8A6", bg: "#F0FDFA", label: "Present" },
  late: { color: "#D97706", bg: "#FFFBEB", label: "Late" },
  half_day: { color: "#D97706", bg: "#FFFBEB", label: "Half day" },
  wfh: { color: "#3B82F6", bg: "#EFF6FF", label: "Work from home" },
  leave: { color: "#8B5CF6", bg: "#F5F3FF", label: "On leave" },
  holiday: { color: "#6366F1", bg: "#EEF2FF", label: "Holiday" },
  weekend: { color: "#9CA3AF", bg: "#F3F4F6", label: "Weekend" },
  absent: { color: "#EF4444", bg: "#FEF2F2", label: "Absent" },
  not_joined: { color: "#9CA3AF", bg: "#F9FAFB", label: "Not joined yet" },
  not_tracked: { color: "#9CA3AF", bg: "#F9FAFB", label: "Not tracked" },
};

// Own header instead of antd's built-in one: the built-in month dropdown opens
// detached from its selector when the page is animating, so these selectors
// open their lists on <body>, directly under themselves.
const YEARS = (() => {
  const y = moment().year();
  return Array.from({ length: 8 }, (_, i) => y - 5 + i);
})();

const CalendarHeader = ({ value, onChange }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 12 }}>
    <Select
      value={value.year()}
      style={{ width: 90 }}
      onChange={(y) => onChange(value.clone().year(y))}
      options={YEARS.map((y) => ({ value: y, label: String(y) }))}
      getPopupContainer={() => document.body}
    />
    <Select
      value={value.month()}
      style={{ width: 120 }}
      onChange={(m) => onChange(value.clone().month(m))}
      options={moment.months().map((name, i) => ({ value: i, label: name }))}
      getPopupContainer={() => document.body}
    />
    <Button onClick={() => onChange(moment())}>This month</Button>
  </div>
);


const fmtDays = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

const LeaveCard = ({ title, color, kpi, year }) => (
  <Card className="i-ems-stat-card" bodyStyle={{ padding: 0 }}>
    <span className="i-ems-stat-accent" style={{ background: color }} />
    <div className="ant-card-body" style={{ padding: "14px 16px" }}>
      <div className="i-ems-stat-label">{title}</div>
      <div className="i-ems-stat-value">
        {fmtDays(kpi.remaining)} <span style={{ fontSize: 14, fontWeight: 400, color: "#6B7280" }}>left of {fmtDays(kpi.entitled)}</span>
      </div>
      <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
        {fmtDays(kpi.taken)} taken in {year}
        {kpi.pending ? ` · ${fmtDays(kpi.pending)} pending` : ""}
        {kpi.prorated ? " · pro-rated (joined this year)" : ""}
      </div>
    </div>
  </Card>
);

// The "Employee calendar" tab of the Attendance page.
// fixedEmployeeId: used inside an employee profile, where the person is already chosen.
const EmployeeCalendarTab = ({ fixedEmployeeId }) => {
  const { request } = useRequest();
  const location = useLocation();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(fixedEmployeeId || location.state?.employeeId);
  const [value, setValue] = useState(moment()); // the month being shown
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const tz = data?.employee?.timezone;
  const time = (v) => formatTimeInTz(v, tz);
  const [downloading, setDownloading] = useState(false);

  // ---- admin correction of one day ----
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [corrAction, setCorrAction] = useState("present");
  const [corrLeaveType, setCorrLeaveType] = useState(undefined);
  const [corrReason, setCorrReason] = useState("");
  const [correcting, setCorrecting] = useState(false);
  // Days without a recorded shift can be corrected; days an admin corrected can be undone.
  const canCorrect = (d) => !!d && (d.corrected || d.presumed || ["absent", "not_tracked"].includes(d.status));

  useEffect(() => {
    request({ url: apiPath.leaveTypes, method: "GET", onSuccess: (res) => setLeaveTypes(res?.data?.data || []) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCorrAction("present");
    setCorrLeaveType(undefined);
    setCorrReason("");
  }, [detail?.date]);

  const submitCorrection = (action) => {
    if (action === "leave" && !corrLeaveType) {
      message.warning("Choose a leave type");
      return;
    }
    setCorrecting(true);
    request({
      url: apiPath.employeeCorrect(employeeId),
      method: "POST",
      data: { date: detail.date, action, leaveType: action === "leave" ? corrLeaveType : undefined, reason: corrReason || undefined },
      onSuccess: () => {
        message.success(action === "undo" ? "Correction removed" : "Day updated");
        setCorrecting(false);
        setDetail(null);
        fetchMonth(employeeId, value);
      },
      onError: (err) => {
        setCorrecting(false);
        message.error(err?.response?.data?.message || err?.message || "Could not update this day");
      },
    });
  };

  // "View calendar" links from the daily table pass the employee in the router state.
  useEffect(() => {
    if (location.state?.employeeId) setEmployeeId(location.state.employeeId);
  }, [location.state?.employeeId]);

  useEffect(() => {
    if (fixedEmployeeId) return;
    request({ url: `${apiPath.employees}?limit=500`, method: "GET", onSuccess: (res) => setEmployees(res?.data?.employees || []) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMonth = (id, m) => {
    if (!id) return;
    request({
      url: `${apiPath.employeeMonth(id)}?year=${m.year()}&month=${m.month() + 1}`,
      method: "GET",
      onSuccess: (res) => setData(res?.data || null),
      onError: () => setData(null),
    });
  };

  useEffect(() => {
    fetchMonth(employeeId, value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, value.year(), value.month()]);

  const dayMap = {};
  (data?.days || []).forEach((d) => { dayMap[d.date] = d; });

  const cellRender = (cellDate) => {
    if (!data || cellDate.month() !== value.month()) return null;
    const d = dayMap[cellDate.format("YYYY-MM-DD")];
    if (!d || d.status === "none") return null;
    const st = STATUS[d.status];
    if (!st) return null;
    let sub = "";
    if (d.status === "leave") sub = d.leaveType || "";
    else if (d.status === "holiday") sub = d.holidayName || "";
    else if (d.presumed) sub = "No shift logged";
    else if (["present", "late", "half_day", "wfh"].includes(d.status) && d.loginAt) sub = `${time(d.loginAt)} - ${time(d.logoutAt)}`;
    return (
      <div
        onClick={() => setDetail(d)}
        style={{ background: st.bg, borderLeft: `3px solid ${st.color}`, borderRadius: 4, padding: "2px 6px", cursor: "pointer", lineHeight: 1.25 }}
      >
        <div style={{ color: st.color, fontSize: 12, fontWeight: 600 }}>{st.label}</div>
        {sub ? <div style={{ color: "#6B7280", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div> : null}
      </div>
    );
  };

  const onDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("i_ems_admin_token") || "";
      const response = await axios.get(`${apiPath.baseURL}${apiPath.employeeMonthExport(employeeId)}?year=${value.year()}&month=${value.month() + 1}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attendance-${data?.employee?.employeeId || "employee"}-${value.format("YYYY-MM")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error("Could not download the month");
    } finally {
      setDownloading(false);
    }
  };

  const s = data?.summary;
  const lb = data?.leaveBalance;

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {fixedEmployeeId ? null : <Select
            showSearch
            placeholder="Select employee"
            style={{ width: 320 }}
            value={employeeId}
            onChange={setEmployeeId}
            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            options={employees.map((e) => ({ value: e._id, label: `${e.name} (${e.employeeId})` }))}
          />}
          {data && !fixedEmployeeId ? (
            <span style={{ color: "#6B7280" }}>
              {data.employee.department ? `${data.employee.department} · ` : ""}
              {data.employee.office ? `${data.employee.office} · ` : ""}
              {data.employee.joiningDate ? `Joined ${moment(data.employee.joiningDate).format("DD MMM YYYY")}` : "Joining date not set"}
            </span>
          ) : null}
          <div style={{ flex: 1 }} />
          <Button icon={<DownloadOutlined />} onClick={onDownload} loading={downloading} disabled={!data}>
            Download {value.format("MMM YYYY")}
          </Button>
        </div>
      </Card>

      {!employeeId ? (
        <Card>
          <EmptyState message="Select an employee to see their month" />
        </Card>
      ) : (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={8} lg={4}><StatCard label="Days present" value={s?.presentDays || 0} color="#14B8A6" /></Col>
            <Col xs={12} md={8} lg={4}><StatCard label="Late" value={s?.lateDays || 0} color="#D97706" /></Col>
            <Col xs={12} md={8} lg={4}><StatCard label="Work from home" value={s?.wfhDays || 0} color="#3B82F6" /></Col>
            <Col xs={12} md={8} lg={4}><StatCard label="On leave" value={s?.leaveDays || 0} color="#8B5CF6" /></Col>
            <Col xs={12} md={8} lg={4}><StatCard label="Absent" value={s?.absentDays || 0} color="#EF4444" /></Col>
            <Col xs={12} md={8} lg={4}><StatCard label="Hours worked" display={String(s?.totalHours ?? 0)} color="#6366F1" /></Col>
          </Row>
          {lb ? (
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={24} md={12}><LeaveCard title="Annual leave" color="#8B5CF6" kpi={lb.annual} year={lb.year} /></Col>
              <Col xs={24} md={12}><LeaveCard title="Sick leave" color="#F59E0B" kpi={lb.sick} year={lb.year} /></Col>
            </Row>
          ) : null}

          <Card>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              {Object.entries(STATUS).map(([k, st]) => (
                <Tag key={k} color={st.bg} style={{ color: st.color, borderColor: st.color }}>{st.label}</Tag>
              ))}
            </div>
            <Calendar
              value={value}
              onPanelChange={(v) => setValue(v)}
              onChange={(v) => { if (v.month() !== value.month() || v.year() !== value.year()) setValue(v); }}
              dateCellRender={cellRender}
              headerRender={({ value: v, onChange }) => <CalendarHeader value={v} onChange={onChange} />}
              fullscreen
            />
          </Card>
        </>
      )}

      <Modal
        open={!!detail}
        title={detail ? `${moment(detail.date).format("dddd, DD MMMM YYYY")}` : ""}
        onCancel={() => setDetail(null)}
        footer={null}
      >
        {detail ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Status">{STATUS[detail.status]?.label || detail.status}</Descriptions.Item>
            {detail.leaveType ? <Descriptions.Item label="Leave type">{detail.leaveType}</Descriptions.Item> : null}
            {detail.reason ? <Descriptions.Item label="Reason">{detail.reason}</Descriptions.Item> : null}
            {detail.holidayName ? <Descriptions.Item label="Holiday">{detail.holidayName}</Descriptions.Item> : null}
            {detail.loginAt ? <Descriptions.Item label="Shift start">{time(detail.loginAt)}</Descriptions.Item> : null}
            {detail.loginAt ? <Descriptions.Item label="Shift end">{detail.logoutAt ? time(detail.logoutAt) : "Still open"}</Descriptions.Item> : null}
            {detail.loginAt ? <Descriptions.Item label="Timezone">{tzLabel(tz)}</Descriptions.Item> : null}
            {detail.hours ? <Descriptions.Item label="Hours">{detail.hours}</Descriptions.Item> : null}
            {detail.note ? <Descriptions.Item label="Note">{detail.note}</Descriptions.Item> : null}
          </Descriptions>
        ) : null}
        {canCorrect(detail) ? (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #E5E7EB" }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Correct this day</div>
            <Radio.Group value={corrAction} onChange={(e) => setCorrAction(e.target.value)} optionType="button" buttonStyle="solid">
              <Radio.Button value="present">Present</Radio.Button>
              <Radio.Button value="wfh">Work from home</Radio.Button>
              <Radio.Button value="leave">On leave</Radio.Button>
            </Radio.Group>
            {corrAction === "leave" ? (
              <Select
                placeholder="Leave type"
                style={{ width: "100%", marginTop: 8 }}
                value={corrLeaveType}
                onChange={setCorrLeaveType}
                options={leaveTypes.map((t) => ({ value: t._id, label: t.name }))}
                getPopupContainer={() => document.body}
              />
            ) : null}
            <Input style={{ marginTop: 8 }} placeholder="Reason (optional)" value={corrReason} onChange={(e) => setCorrReason(e.target.value)} />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Button type="primary" loading={correcting} onClick={() => submitCorrection(corrAction)}>Save</Button>
              {detail.corrected ? (
                <Popconfirm title="Remove this correction?" onConfirm={() => submitCorrection("undo")}>
                  <Button danger disabled={correcting}>Undo correction</Button>
                </Popconfirm>
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default EmployeeCalendarTab;
